import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner, } from "@/Components/ui";
;
import toast, { Toaster } from 'react-hot-toast';
import Select from "react-select";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ServerPaginatedTable from "../../Components/Common/ServerPaginatedTable";
import { useTemplates } from "../../hooks/useMessaging";
import { siteService } from "../../api/services/site.service";
import { SITE_ID, SUBDOMAIN } from "../../assets/meta/meta";
import { messagingService } from "../../api";
import { TEMPLATE_CATEGORIES, CATEGORY_GROUPS } from "../../utils/kakao_template_category";
import ButtonsEditor from "../../Components/Common/ButtonsEditor";
import ImageUploader from "../../Components/Common/ImageUploader";
import { dialogService } from "@/utils/dialogService";
const TemplateManagement = () => {
    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState({
        id: SITE_ID, subdomain: SUBDOMAIN
    });
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    // Modal states
    const [modal, setModal] = useState(false);
    const [previewModal, setPreviewModal] = useState(false);
    const [detailModal, setDetailModal] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [formData, setFormData] = useState({
        template_code: '',
        template_name: '',
        template_content: '',
        template_type: 'BA',
        category_code: '008001',
        is_secure_template: false,
        buttons: [],
        quick_links: [],
        image: null,
    });
    const { templates, pagination, loading, error, fetchTemplates, createTemplate, updateTemplate, deleteTemplate, uploadImage } = useTemplates();
    // Fetch sites on component mount
    useEffect(() => {
        const fetchSites = async () => {
            try {
                const response = await siteService.getSitesApp();
                setSites(response.data.result.sites);
            }
            catch (error) {
                console.error("Failed to fetch sites:", error);
                toast.error("사이트 목록을 불러오는데 실패했습니다.");
            }
        };
        fetchSites();
    }, []);
    const handleFilterChange = () => {
        const filters = {};
        if (selectedSite)
            filters.site_id = selectedSite.id;
        if (statusFilter)
            filters.template_status = statusFilter;
        if (typeFilter)
            filters.template_type = typeFilter;
        if (searchTerm)
            filters.search = searchTerm;
        fetchTemplates(filters);
    };
    const toggleModal = () => {
        setModal(!modal);
        if (!modal) {
            setEditingTemplate(null);
            setFormData({
                template_code: '',
                template_name: '',
                template_content: '',
                template_type: 'BA',
                category_code: '008001',
                is_secure_template: false,
                buttons: [],
                quick_links: [],
                image: null,
            });
        }
    };
    const togglePreview = () => {
        setPreviewModal(!previewModal);
    };
    const handleEdit = (template) => {
        // Map template structure into our form fields. Some backends use different names.
        setEditingTemplate(template);
        setFormData({
            template_code: template.template_code || '',
            template_name: template.template_name || '',
            template_content: template.template_content || '',
            template_type: template.template_type || 'BA',
            category_code: template.category_code || '008001',
            is_secure_template: !!template.is_secure_template,
            buttons: template.buttons || [],
            quick_links: template.quick_links || [],
            image: template.image || null,
        });
        setModal(true);
    };
    const handleDelete = async (templateId) => {
        if (!await dialogService.confirm("정말로 이 템플릿을 삭제하시겠습니까?"))
            return;
        try {
            await deleteTemplate(templateId);
            toast.success("템플릿이 삭제되었습니다.");
            handleFilterChange();
        }
        catch (error) {
            toast.error(error.message || "템플릿 삭제에 실패했습니다.");
        }
    };
    const generateTemplateCode = () => {
        // simple client generator: slug of name + timestamp suffix, max 30 chars
        const base = (formData.template_name || 'tmpl').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        const suffix = Date.now().toString().slice(-5);
        const code = `${base}`.slice(0, 24 - suffix.length) + '_' + suffix;
        setFormData({ ...formData, template_code: code.slice(0, 30) });
    };
    const buildPayloadForSubmit = () => {
        // Build a backend-friendly payload. Exclude fields the backend will handle.
        const payload = {
            template_code: formData.template_code,
            template_name: formData.template_name,
            template_type: formData.template_type,
            category_code: formData.category_code,
            is_secure_template: formData.is_secure_template,
            template_content: formData.template_content,
            buttons: formData.buttons || [],
            quick_links: formData.quick_links || [],
            site_id: selectedSite?.id,
        };
        if (formData.image && formData.image.id) {
            payload.image_id = formData.image.id;
        }
        return payload;
    };
    const showTemplateDetails = async (id) => {
        try {
            const resp = await messagingService.getTemplate(id);
            if (resp && resp.data) {
                setDetailData({ template: resp.data });
                setDetailModal(true);
            }
            else {
                toast.error('템플릿 상세 정보를 불러오는데 실패했습니다.');
            }
        }
        catch (err) {
            console.error('showTemplateDetails error', err);
            toast.error('템플릿 상세 정보를 불러오는데 실패했습니다.');
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedSite) {
            toast.error("사이트를 선택해주세요.");
            return;
        }
        if (!formData.template_name || !formData.template_content) {
            toast.error("템플릿명과 템플릿 내용을 입력해주세요.");
            return;
        }
        if ((formData.template_content || '').length > 1000) {
            toast.error("템플릿 내용은 1000자 이내로 입력해주세요.");
            return;
        }
        try {
            const data = buildPayloadForSubmit();
            if (editingTemplate) {
                await updateTemplate(editingTemplate.id, data);
                toast.success("템플릿이 수정되었습니다.");
            }
            else {
                await createTemplate(data);
                toast.success("템플릿이 생성되었습니다.");
            }
            toggleModal();
            handleFilterChange();
        }
        catch (error) {
            // Try to surface structured errors if backend provided them
            if (error?.response?.data) {
                const resp = error.response.data;
                // generic mapping
                if (typeof resp === 'object') {
                    const messages = [];
                    Object.entries(resp).forEach(([k, v]) => {
                        messages.push(`${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
                    });
                    toast.error(messages.join(' | '));
                }
                else {
                    toast.error(resp);
                }
            }
            else {
                toast.error(error.message || "템플릿 저장에 실패했습니다.");
            }
        }
    };
    const getStatusBadge = (status) => {
        const statuses = {
            'REG': { label: '등록', color: 'secondary' },
            'REQ': { label: '검수요청', color: 'warning' },
            'APR': { label: '승인', color: 'success' },
            'REJ': { label: '반려', color: 'danger' },
            'STP': { label: '중단', color: 'dark' }
        };
        const statusInfo = statuses[status] || { label: status, color: 'secondary' };
        return <Badge color={statusInfo.color}>{statusInfo.label}</Badge>;
    };
    const getTypeBadge = (type) => {
        const types = {
            'BA': { label: '기본형', color: 'primary' },
            'EX': { label: '부가정보형', color: 'info' },
            'AD': { label: '광고추가형', color: 'warning' },
            'MI': { label: '복합형', color: 'success' },
        };
        const typeInfo = types[type] || { label: type, color: 'secondary' };
        return <Badge color={typeInfo.color}>{typeInfo.label}</Badge>;
    };
    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString('ko-KR');
    };
    const columns = [
        {
            Header: "ID",
            accessor: "id",
            enableColumnFilter: false,
            size: 80,
        },
        {
            Header: "템플릿명",
            accessor: "template_name",
            enableColumnFilter: false,
        },
        {
            Header: "템플릿 코드",
            accessor: "template_code",
            enableColumnFilter: false,
            size: 120,
        },
        {
            Header: "유형",
            accessor: "template_type",
            enableColumnFilter: false,
            cell: ({ row }) => getTypeBadge(row.original.template_type),
            size: 100,
        },
        {
            Header: "카테고리",
            accessor: "category_code",
            enableColumnFilter: false,
            cell: ({ row }) => {
                const category = TEMPLATE_CATEGORIES.find(cat => cat.code === row.original.category_code);
                return category ? category.name : row.original.category_code;
            },
            size: 120,
        },
        {
            Header: "상태",
            accessor: "template_status",
            enableColumnFilter: false,
            cell: ({ row }) => getStatusBadge(row.original.template_status),
            size: 120,
        },
        {
            Header: "내용",
            accessor: "template_content",
            enableColumnFilter: false,
            cell: ({ row }) => (<div className="text-truncate" style={{ maxWidth: '200px' }}>
          {row.original.template_content}
        </div>),
        },
        {
            Header: "생성일",
            accessor: "created_at",
            enableColumnFilter: false,
            cell: ({ row }) => formatDateTime(row.original.created_at),
            size: 150,
        },
        {
            Header: "작업",
            accessor: "actions",
            enableColumnFilter: false,
            enableSorting: false,
            Cell: ({ row }) => (<UncontrolledDropdown>
          <DropdownToggle tag="a" className="btn btn-soft-secondary btn-sm">
            <i className="ri-more-fill"></i>
          </DropdownToggle>
          <DropdownMenu className="dropdown-menu-end">
            <DropdownItem onClick={() => handleEdit(row.original)}>
              <i className="ri-pencil-line align-bottom me-2 text-muted"></i>
              수정
            </DropdownItem>
            <DropdownItem divider/>
            <DropdownItem onClick={() => showTemplateDetails(row.original.id)}>
              <i className="ri-eye-line align-bottom me-2 text-muted"></i>
              상세보기
            </DropdownItem>
            <DropdownItem onClick={() => handleDelete(row.original.id)}>
              <i className="ri-delete-bin-line align-bottom me-2 text-muted"></i>
              삭제
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>),
            size: 100,
        },
    ];
    const siteOptions = sites.map(site => ({
        value: site.id,
        label: `${site.name} (${site.subdomain})`,
        site: site,
    }));
    const statusOptions = [
        { value: 'PENDING', label: '승인대기' },
        { value: 'APR', label: '승인완료' },
        { value: 'REJ', label: '승인거부' },
        { value: 'STOP', label: '사용중지' }
    ];
    const typeOptions = [
        { value: 'AT', label: '기본형 (AlimTalk)' },
        { value: 'AI', label: '이미지형 (AlimTalk Image)' },
        { value: 'EX', label: '부가정보형' },
        { value: 'AD', label: '광고추가형' },
        { value: 'MI', label: '복합형 (Mixed)' }
    ];
    return (<React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="템플릿 관리" pageTitle="메시징"/>

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="card-title mb-0">카카오 템플릿 관리</h5>
                    </Col>
                    <Col className="col-auto">
                      <Button color="primary" size="sm" onClick={toggleModal}>
                        <i className="ri-add-line align-bottom me-1"></i>
                        템플릿 추가
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <Select value={siteOptions.find(option => option.value === selectedSite?.id)} onChange={(option) => setSelectedSite(option?.site || null)} options={siteOptions} placeholder="사이트 선택" 
    // isClearable
    isDisabled/>
                    </Col>
                    <Col md={2}>
                      <Select value={statusOptions.find(option => option.value === statusFilter)} onChange={(option) => setStatusFilter(option?.value || '')} options={statusOptions} placeholder="상태" isClearable/>
                    </Col>
                    <Col md={2}>
                      <Select value={typeOptions.find(option => option.value === typeFilter)} onChange={(option) => setTypeFilter(option?.value || '')} options={typeOptions} placeholder="유형" isClearable/>
                    </Col>
                    <Col md={3}>
                      <Input type="text" placeholder="템플릿명 검색" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}/>
                    </Col>
                    <Col md={2}>
                      <Button color="primary" onClick={handleFilterChange} disabled={loading}>
                        {loading ? <Spinner size="sm"/> : <i className="ri-search-line"></i>}
                      </Button>
                    </Col>
                  </Row>

                  {error && (<div className="alert alert-danger">
                      <strong>오류:</strong> {error}
                    </div>)}

                  <ServerPaginatedTable columns={columns} data={templates || []} previous={pagination?.previous} next={pagination?.next} count={pagination?.count || 0} onRequestData={(url) => {
            const urlParams = new URL(url, window.location.origin);
            const page = urlParams.searchParams.get('page');
            const filters = {};
            if (selectedSite)
                filters.site_id = selectedSite.id;
            if (statusFilter)
                filters.template_status = statusFilter;
            if (typeFilter)
                filters.template_type = typeFilter;
            if (searchTerm)
                filters.search = searchTerm;
            if (page)
                filters.page = page;
            fetchTemplates(filters);
        }} loading={loading}/>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={modal} onClose={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          {editingTemplate ? '템플릿 수정' : '템플릿 추가'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>템플릿명 *</Label>
                  <Input type="text" value={formData.template_name} onChange={(e) => setFormData({ ...formData, template_name: e.target.value })} required/>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>템플릿 유형 *</Label>
                  <Input type="select" value={formData.template_type} onChange={(e) => setFormData({ ...formData, template_type: e.target.value })} required>
                    <option value="BA">기본형 (Basic)</option>
                    <option value="EX">부가정보형 (Extra)</option>
                    <option value="AD">광고추가형 (Ad)</option>
                    <option value="MI">복합형 (Mixed)</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>템플릿 코드 *</Label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Input type="text" value={formData.template_code} onChange={(e) => setFormData({ ...formData, template_code: e.target.value })} placeholder="템플릿코드 입력 (최대 30자)" maxLength={30} required/>
                    <Button color="outline-primary" onClick={generateTemplateCode}>코드 자동 생성</Button>
                  </div>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>보안템플릿</Label>
                  <div className="form-check form-switch">
                    <Input type="checkbox" checked={formData.is_secure_template} onChange={(e) => setFormData({ ...formData, is_secure_template: e.target.checked })} className="form-check-input"/>
                    <small className="form-text text-muted">보안 템플릿 설정 시, 메인 디바이스 모바일 외 모든 서브 디바이스에서는 메시지 내용이 노출되지 않습니다.</small>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>템플릿 내용 *</Label>
              <Input type="textarea" rows={8} value={formData.template_content} onChange={(e) => setFormData({ ...formData, template_content: e.target.value })} placeholder="템플릿 내용을 입력하세요&#10;#{변수명} 형태로 변수를 사용할 수 있습니다. (예 : #{홍길동}님의 택배가 금일 (#{09:50})에 배달될 예정입니다.)" required/>
              <div className="text-muted small mt-1">
                현재 길이: {formData.template_content.length}자 (최대 1000자)
              </div>
            </FormGroup>

            <FormGroup>
              <Label>카테고리 코드</Label>
              <Input type="select" value={formData.category_code} onChange={(e) => setFormData({ ...formData, category_code: e.target.value })}>
                {Object.entries(CATEGORY_GROUPS).map(([groupName, categories]) => (<optgroup key={groupName} label={groupName}>
                    {categories.map((category) => (<option key={category.code} value={category.code}>
                        {category.code} - {category.name}
                      </option>))}
                  </optgroup>))}
              </Input>
              <div className="text-muted small mt-1">
                선택된 카테고리: {TEMPLATE_CATEGORIES[formData.category_code]?.name || '알 수 없음'}
              </div>
            </FormGroup>

            <FormGroup>
              <Label>이미지 (선택)</Label>
              <ImageUploader siteId={selectedSite?.id} value={formData.image} onChange={(img) => setFormData({ ...formData, image: img })} uploader={uploadImage}/>
            </FormGroup>

            <FormGroup>
              <Label>버튼</Label>
              <ButtonsEditor channel="AT" value={formData.buttons} onChange={(buttons) => setFormData({ ...formData, buttons })} externalError={null}/>
            </FormGroup>

            <FormGroup>
              <Label>바로연결 (Quick Links)</Label>
              <ButtonsEditor channel="AT" value={formData.quick_links} onChange={(quick_links) => setFormData({ ...formData, quick_links })} externalError={null}/>
            </FormGroup>

            <div className="d-flex justify-content-end">
              <Button color="secondary" outline className="me-2" onClick={() => togglePreview()}>
                JSON 변환
              </Button>
            </div>

          </ModalBody>
          <ModalFooter>
            <Button type="button" color="secondary" onClick={toggleModal}>
              취소
            </Button>
            <Button type="submit" color="primary" disabled={loading}>
              {loading && <Spinner size="sm" className="me-1"/>}
              {editingTemplate ? '수정' : '생성'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* JSON Preview Modal */}
      <Modal isOpen={previewModal} onClose={togglePreview} size="lg">
        <ModalHeader toggle={togglePreview}>생성/수정 미리보기 (JSON)</ModalHeader>
        <ModalBody>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(buildPayloadForSubmit(), null, 2)}
          </pre>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={togglePreview}>닫기</Button>
        </ModalFooter>
      </Modal>

      {/* Detail Modal */}
      <Modal isOpen={detailModal} onClose={() => setDetailModal(!detailModal)} size="lg">
        <ModalHeader toggle={() => setDetailModal(!detailModal)}>템플릿 상세</ModalHeader>
        <ModalBody>
          {!detailData && <div>로딩 중...</div>}
          {detailData && (<div>
              <h5>템플릿 상태: <strong>{detailData.template?.template_status || detailData.template?.template_json?.templateStatus || 'N/A'}</strong></h5>
              <hr />
              <h6>검수 코멘트</h6>
              {detailData.api_response?.data?.templateComments && detailData.api_response.data.templateComments.length > 0 ? (<div>
                  {detailData.api_response.data.templateComments.map((c) => (<div key={c.commentSeqno} style={{ marginBottom: 12, padding: 8, border: '1px solid #eee', borderRadius: 4 }}>
                      <div style={{ fontSize: 13, color: '#333' }}><strong>{c.commentUserName || c.regBy}</strong> <span style={{ color: '#888', marginLeft: 8 }}>{c.commentCreateAt || c.regDate}</span></div>
                      <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{c.commentContent}</div>
                      <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>상태: {c.commentStatus}</div>
                    </div>))}
                </div>) : (<div>검수 코멘트가 없습니다.</div>)}
            </div>)}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDetailModal(false)}>닫기</Button>
        </ModalFooter>
      </Modal>

      <Toaster position="top-center"/>
    </React.Fragment>);
};
export default TemplateManagement;
